import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Empty, IUserProps, LoaderDots, ModalContent, ModalTitle, UserList, UserListItem } from 'components';
import { LoadButton, SearchInput, SelectUserModal, SelectUserModalHeader } from './styles';

interface SelectUserProps {
  isActive: boolean;
  onSelect: (id: string) => void;
  onClose: () => void;
  loadMore: () => void;
  onNameChange: (name: string) => void;
  isLoading?: boolean;
  limit: number;
  title?: string;
  users: Array<IUserProps>;
  disabledToolTip?: string;
}

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  height: 50%;
`;

const SelectUser = ({ disabledToolTip, isActive, onSelect, isLoading, limit = 10, loadMore, onClose, onNameChange, title = 'Add Existing Employee', users }: SelectUserProps) => {
  const loadButtonVisible = users?.length > limit;
  const [searchName, setSearchName] = useState('');

  useEffect(() => {
    onNameChange(searchName);
  }, [searchName]);

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (!!e.target.value && !e.target.value.trim()) return;
      setSearchName(e.target.value);
    },
    [onNameChange]
  );

  return (
    <SelectUserModal maxWidth={540} isActive={isActive} onClose={onClose} data-cy="select-user-modal">
      <SelectUserModalHeader>
        <ModalTitle data-cy="modal-title">{title}</ModalTitle>
      </SelectUserModalHeader>
      <ModalContent>
        <SearchInput autoFocus value={searchName} onChange={onChange} placeholder="Search by name." tabIndex={1} disabled={isLoading} data-cy="select-user-search-input" />

        <UserList itemsVisible={5.5}>
          {isLoading ? (
            <LoadingContainer>
              <LoaderDots />
            </LoadingContainer>
          ) : (
            <>
              {users && users.length === 0 && <Empty icon="People" message="No employees matching name found" data-cy="empty-icon" />}

              {users &&
                users.length > 0 &&
                users
                  .slice(0, limit)
                  .map((user: IUserProps, index: number) => (
                    <UserListItem
                      key={index}
                      username={user.username}
                      image={user.image}
                      site={user.site}
                      hasPaymentMethod={user.hasPaymentMethod}
                      id={user.id}
                      role={user.role}
                      disabled={user.disabled}
                      onSelect={() => onSelect(user.id)}
                      data-cy="existing-user-item"
                      disabledToolTip={disabledToolTip || user.disabledReason}
                    />
                  ))}

              {loadButtonVisible && <LoadButton onClick={loadMore} label="Load More" appearance="primary" size="small" data-cy="load-more-btn" />}
            </>
          )}
        </UserList>
      </ModalContent>
    </SelectUserModal>
  );
};

export default SelectUser;
